/**
 * ForcePasswordChangePage - Requires users to set a new password before
 * accessing the application.
 *
 * Shown when the user's account has must_change_password = true, which happens:
 *	 1. On first login after an admin creates the user account
 *	 2. After an admin or super_admin resets the user's password
 *
 * The user must enter their current (temporary) password and a new password
 * that meets the platform's password policy. After successful change, the
 * must_change_password flag is cleared and the user is redirected to the dashboard.
 *
 * This page is rendered OUTSIDE the AppLayout (no nav/sidebar) to prevent
 * the user from navigating away without changing their password.
 *
 * Accessible by: authenticated users with must_change_password = true. A
 * Super Admin signed in as such a user (Sign In As) is sent to the dashboard
 * instead: the new password is the user's to choose.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { Alert, BrandWordmark, Button } from '../../components/atoms';

/* ============================================
 * PASSWORD VALIDATION RULES
 * Same policy as ProfilePage — keeps password requirements consistent.
 * ============================================ */

interface PasswordRule {
	label: string;
	test: (pw: string) => boolean;
}

/** Password policy: 12+ chars, uppercase, lowercase, number, special character */
const PASSWORD_RULES: PasswordRule[] = [
	{ label: 'At least 12 characters', test: (pw) => pw.length >= 12 },
	{ label: 'At least one uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
	{ label: 'At least one lowercase letter', test: (pw) => /[a-z]/.test(pw) },
	{ label: 'At least one number', test: (pw) => /[0-9]/.test(pw) },
	{ label: 'At least one special character', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

/* ============================================
 * COMPONENT
 * ============================================ */

const ForcePasswordChangePage: React.FC = () => {
	const navigate = useNavigate();
	const { user, isLoading, updateUser, logout, passwordChangeDue } = useAuth();

	/* --- Form state --- */
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);

	/* --- Feedback state --- */
	const [error, setError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	/* Guard: this route lives OUTSIDE <ProtectedRoute>, so enforce access here.
	 * Once auth finishes restoring, send unauthenticated visitors to /login and
	 * users who don't actually need a password change to the dashboard — instead
	 * of showing the form (which would 401 on submit) to either. */
	useEffect(() => {
		if (isLoading) return;
		if (!user) {
			navigate('/login', { replace: true });
		} else if (!passwordChangeDue) {
			navigate('/', { replace: true });
		}
	}, [isLoading, user, passwordChangeDue, navigate]);

	/**
	 * Submits the password change form via POST /auth/change-password.
	 * The backend clears the must_change_password flag on success.
	 */
	const handleSubmit = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		/* Client-side validation: all password rules must pass */
		const allRulesPass = PASSWORD_RULES.every((rule) => rule.test(newPassword));
		if (!allRulesPass) {
			setError('New password does not meet all requirements.');
			return;
		}

		/* Client-side validation: passwords must match */
		if (newPassword !== confirmPassword) {
			setError('New password and confirmation do not match.');
			return;
		}

		setIsSaving(true);

		try {
			await api.post('/auth/change-password', {
				current_password: currentPassword,
				new_password: newPassword,
				new_password_confirmation: confirmPassword,
			});

			/* Clear the flag in frontend state so ProtectedRoute lets them through */
			updateUser({ mustChangePassword: false });

			/* Navigate to the dashboard */
			navigate('/', { replace: true });
		} catch (err: any) {
			const errorText = err?.data?.message || 'Failed to change password. Please check your current password.';
			setError(errorText);
		} finally {
			setIsSaving(false);
		}
	}, [currentPassword, newPassword, confirmPassword, updateUser, navigate]);

	/* --- Shared input classes --- */
	const inputClass = 'w-full px-12px py-10px text-16px font-sans rounded-md border border-brand-border outline-none focus:ring-2 focus:ring-brand-primary focus:ring-opacity-20 focus:border-brand-primary bg-white';

	/* While auth restores or a redirect is pending, don't flash the form. */
	if (isLoading || !user || !passwordChangeDue) {
		return (
			<div className="force-password-change-page min-h-screen min-h-[100dvh] flex items-center justify-center bg-[#eae5e5] p-16px" data-ref="page-force-password-change-page">
				<p className="text-brand-text font-sans">Loading...</p>
			</div>
		);
	}

	return (
		<div className="force-password-change-page min-h-screen min-h-[100dvh] flex items-center justify-center bg-[#eae5e5] p-16px" data-ref="page-force-password-change-page">
			<div className="max-w-md w-full">
				{/* --- Branding header --- */}
				<div className="text-center mb-24px">
					<BrandWordmark as="h1" size="xl" className="force-password-change-page__brand font-bold" />
					<p className="mt-8px text-14px font-sans text-brand-text-muted">Work Order &amp; Inspection Management</p>
				</div>

				{/* --- Password change card --- */}
				<div className="bg-white py-32px px-24px shadow-lg rounded-lg">
					{/* Header explaining why the user must change their password */}
					<div className="mb-20px">
						<h2 className="text-18px font-bold font-sans text-brand-text">
							Password Change Required
						</h2>
						<p className="mt-4px text-14px text-brand-text-muted font-sans">
							{user?.firstName ? `Welcome, ${user.firstName}! ` : ''}
							For your security, please set a new password before continuing.
						</p>
					</div>

					{/* Error banner */}
					{error && (
						<Alert variant="error" className="mb-16px">{error}</Alert>
					)}

					<form onSubmit={handleSubmit} className="space-y-16px">
						{/* Current (temporary) password field */}
						<div>
							<label htmlFor="force-current-pw" className="block text-14px font-medium font-sans text-brand-text mb-4px">
								Current Password
							</label>
							<div className="relative">
								<input
									id="force-current-pw"
									type={showCurrentPassword ? 'text' : 'password'}
									value={currentPassword}
									onChange={(e) => setCurrentPassword(e.target.value)}
									required
									placeholder="Enter your current password"
									className={`${inputClass} pr-44px`}
								/>
								{/* Visibility toggle */}
								<button
									type="button"
									onClick={() => setShowCurrentPassword(!showCurrentPassword)}
									className="absolute inset-y-0 right-0 flex items-center pr-12px text-gray-400 hover:text-gray-600"
									aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
								>
									{showCurrentPassword ? (
										<svg xmlns="http://www.w3.org/2000/svg" className="h-18px w-18px" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
									) : (
										<svg xmlns="http://www.w3.org/2000/svg" className="h-18px w-18px" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
									)}
								</button>
							</div>
						</div>

						{/* New password field */}
						<div>
							<label htmlFor="force-new-pw" className="block text-14px font-medium font-sans text-brand-text mb-4px">
								New Password
							</label>
							<div className="relative">
								<input
									id="force-new-pw"
									type={showNewPassword ? 'text' : 'password'}
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
									required
									placeholder="Create a new password"
									className={`${inputClass} pr-44px`}
								/>
								{/* Visibility toggle */}
								<button
									type="button"
									onClick={() => setShowNewPassword(!showNewPassword)}
									className="absolute inset-y-0 right-0 flex items-center pr-12px text-gray-400 hover:text-gray-600"
									aria-label={showNewPassword ? 'Hide password' : 'Show password'}
								>
									{showNewPassword ? (
										<svg xmlns="http://www.w3.org/2000/svg" className="h-18px w-18px" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
									) : (
										<svg xmlns="http://www.w3.org/2000/svg" className="h-18px w-18px" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
									)}
								</button>
							</div>

							{/* Password requirements checklist — shown when user starts typing */}
							{newPassword.length > 0 && (
								<ul className="mt-8px space-y-2px">
									{PASSWORD_RULES.map((rule) => {
										const passes = rule.test(newPassword);
										return (
											<li
												key={rule.label}
												className={`flex items-center gap-6px text-12px font-sans ${passes ? 'text-green-600' : 'text-brand-text-subtle'}`}
											>
												{/* Checkmark or circle icon */}
												{passes ? (
													<svg className="w-12px h-12px flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
												) : (
													<svg className="w-12px h-12px flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><circle cx="12" cy="12" r="9" /></svg>
												)}
												{rule.label}
											</li>
										);
									})}
								</ul>
							)}
						</div>

						{/* Confirm new password field */}
						<div>
							<label htmlFor="force-confirm-pw" className="block text-14px font-medium font-sans text-brand-text mb-4px">
								Confirm New Password
							</label>
							<input
								id="force-confirm-pw"
								type="password"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								required
								placeholder="Confirm your new password"
								className={inputClass}
							/>
							{/* Mismatch warning — shown when both fields have content and don't match */}
							{confirmPassword.length > 0 && newPassword !== confirmPassword && (
								<p className="mt-4px text-12px text-red-600 font-sans">Passwords do not match.</p>
							)}
						</div>

						{/* Submit button */}
						<Button type="submit" variant="primary" size="sm" fullWidth disabled={isSaving} className="shadow-sm">
							{isSaving ? 'Updating Password...' : 'Set New Password'}
						</Button>
					</form>

					{/* Sign out link — allows user to log out if they don't know the temp password */}
					<div className="mt-16px text-center">
						<button
							type="button"
							onClick={logout}
							className="inline-flex items-center justify-center min-h-44px px-8px text-14px text-brand-text-subtle hover:text-brand-text font-sans transition-colors"
						>
							Sign out
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ForcePasswordChangePage;
